<template>
  <div class="skill-chat">
    <header class="skill-chat__head">
      <div>
        <div class="skill-chat__eyebrow">
          <span class="skill-chat__eyebrow-mark" />
          <span>Skill Creator</span>
        </div>
        <h2 class="skill-chat__title">Author with AI</h2>
      </div>
      <button class="skill-chat__close" title="Close" @click="$emit('close')">
        <Icon name="chevron-right" :size="18" />
      </button>
    </header>

    <div ref="scroll" class="skill-chat__messages">
      <div v-if="!messages.length" class="skill-chat__intro">
        <p>
          Tell me about the UX task you want to turn into a skill — what should
          Alice do, and when should it kick in? I'll interview you and draft a
          best-practice skill you can save.
        </p>
        <p class="skill-chat__intro-hint">
          e.g. “A skill that reviews a draft UX research report and flags weak
          evidence and unclear insights.”
        </p>
      </div>

      <div
        v-for="(m, i) in messages"
        :key="i"
        class="skill-chat__msg"
        :class="`skill-chat__msg--${m.role}`"
      >
        <div class="skill-chat__msg-role">
          {{ m.role === "user" ? "You" : "Skill Creator" }}
        </div>
        <div class="skill-chat__msg-body">{{ displayText(m) }}</div>
        <div
          v-if="m.attachments && m.attachments.length"
          class="skill-chat__msg-attachments"
        >
          <a
            v-for="(att, ai) in m.attachments"
            :key="ai"
            :href="att.url"
            target="_blank"
            rel="noopener"
            class="skill-chat__msg-attachment"
          >
            <Icon
              :name="
                (att.contentType || '').startsWith('image/') ? 'image' : 'file'
              "
              :size="12"
            />
            <span>{{ att.name }}</span>
          </a>
        </div>
        <div
          v-if="m.role === 'assistant' && extractSkill(m.content)"
          class="skill-chat__save-row"
        >
          <button class="skill-chat__save" @click="saveFrom(m.content)">
            <Icon name="plus" :size="14" />
            <span>Save as skill</span>
          </button>
        </div>
      </div>

      <div v-if="sending" class="skill-chat__msg skill-chat__msg--assistant">
        <div class="skill-chat__msg-role">Skill Creator</div>
        <div class="skill-chat__msg-body">
          <ThinkingDots :size="6" />
        </div>
      </div>
    </div>

    <div v-if="error" class="skill-chat__error">{{ error }}</div>

    <div
      v-if="pendingAttachments.length"
      class="skill-chat__pending"
      aria-label="Attachments to send"
    >
      <span
        v-for="(att, idx) in pendingAttachments"
        :key="idx"
        class="skill-chat__pending-chip"
      >
        <img
          v-if="att.previewUrl"
          :src="att.previewUrl"
          class="skill-chat__pending-chip-thumb"
          alt=""
        />
        <Icon
          v-else
          :name="att.contentType.startsWith('image/') ? 'image' : 'file'"
          :size="12"
        />
        <span class="skill-chat__pending-chip-name">{{ att.name }}</span>
        <button
          v-if="!uploadingAttachments"
          type="button"
          class="skill-chat__pending-chip-x"
          :title="`Remove ${att.name}`"
          @click="removePending(idx)"
        >
          ×
        </button>
        <span
          v-if="uploadingAttachments"
          class="skill-chat__pending-chip-progress"
          :style="{ width: Math.round((att.progress || 0) * 100) + '%' }"
        />
      </span>
    </div>

    <form class="skill-chat__input" @submit.prevent="send">
      <input
        ref="fileInput"
        type="file"
        multiple
        accept="application/pdf,image/png,image/jpeg,image/webp,image/gif"
        class="skill-chat__file-input"
        @change="onFilePick"
      />
      <button
        type="button"
        class="skill-chat__attach"
        title="Attach a document or image (PDF, PNG, JPG)"
        :disabled="sending"
        @click="$refs.fileInput.click()"
      >
        <Icon name="paperclip" :size="16" />
      </button>
      <textarea
        v-model="input"
        class="skill-chat__textarea"
        rows="2"
        placeholder="Describe the skill, or answer the question…"
        :disabled="sending"
        @keydown.enter.exact.prevent="send"
        @paste="handlePaste"
      />
      <button
        type="submit"
        class="skill-chat__send"
        :disabled="sending || (!input.trim() && !pendingAttachments.length)"
      >
        <Icon name="arrow-right" :size="16" />
      </button>
    </form>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { httpsCallable } from "firebase/functions";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { functions, storage } from "@/firebase";
import Icon from "@/components/alice/Icon.vue";
import ThinkingDots from "@/components/alice/ThinkingDots.vue";

// Pull the fenced ```skill ... ``` block the assistant emits and parse its
// frontmatter into { name, description, body }. Mirrors the backend
// modules/skills/format.js parseSkillMarkdown (kept small and dependency-free
// since the frontend can't require the Cloud Functions module). Returns null
// when the message doesn't contain a complete skill block yet.
function extractSkillBlock(text) {
  if (!text) return null;
  const fence = text.match(/```(?:skill|markdown|md)?\s*\n([\s\S]*?)```/i);
  const raw = fence ? fence[1] : text;
  const fm = raw.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!fm) return null;

  const [, front, body] = fm;
  const skill = { name: "", description: "", body: body.trim() };
  const lines = front.split("\n");
  for (let i = 0; i < lines.length; i += 1) {
    const kv = lines[i].match(/^(\w[\w-]*):\s*(.*)$/);
    if (!kv) continue;
    let value = kv[2];
    if (/^\|-?\+?$/.test(value.trim())) {
      const block = [];
      while (i + 1 < lines.length && /^\s{2,}/.test(lines[i + 1])) {
        i += 1;
        block.push(lines[i].replace(/^\s{2}/, ""));
      }
      value = block.join("\n").trim();
    } else {
      value = value.trim().replace(/^"(.*)"$/, "$1");
    }
    if (kv[1] === "name") skill.name = value;
    else if (kv[1] === "description") skill.description = value;
  }
  if (!skill.name && !skill.description) return null;
  return skill;
}

export default {
  name: "SkillAuthoringChat",
  components: { Icon, ThinkingDots },
  props: {
    // Optional current draft, handed to the model so it edits rather than
    // starting from scratch.
    draft: { type: Object, default: null },
    // Optional first message, sent as soon as it arrives: { key, text }. The
    // Feedback tab uses it to hand over the open feedback ("Process with AI").
    seed: { type: Object, default: null },
  },
  emits: ["save", "close"],
  data() {
    return {
      messages: [],
      input: "",
      sending: false,
      error: "",
      // Files staged for the next message (same shape as ProjectChat):
      // { file, name, contentType, progress, previewUrl }
      pendingAttachments: [],
      uploadingAttachments: false,
    };
  },
  computed: {
    ...mapState({ user: (state) => state.user }),
  },
  watch: {
    "seed.key"() {
      this.sendSeed();
    },
  },
  mounted() {
    this.sendSeed();
  },
  methods: {
    sendSeed() {
      const text = this.seed && this.seed.text;
      if (!text || this.sending) return;
      this.input = text;
      this.send();
    },
    extractSkill(text) {
      return extractSkillBlock(text);
    },
    // User messages with attachments carry `text` for display (their `content`
    // is a block array for the API); everything else displays `content`.
    displayText(m) {
      return m.text != null ? m.text : m.content;
    },
    onFilePick(event) {
      const files = Array.from(event.target?.files || []);
      this.addAttachmentFiles(files);
      // Reset so the same file can be picked again later
      if (event.target) event.target.value = "";
    },
    handlePaste(event) {
      const clip = event.clipboardData;
      if (!clip) return;
      // Clipboard carries text: paste as text
      if (clip.getData("text/plain").trim()) return;
      const files = Array.from(clip.items || [])
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter(Boolean);
      if (!files.length) return;
      event.preventDefault();
      this.addAttachmentFiles(files, { synthesizeNames: true });
    },
    addAttachmentFiles(files, { synthesizeNames = false } = {}) {
      const accepted = files.filter(
        (f) => f.type === "application/pdf" || f.type.startsWith("image/")
      );
      const stamp = Date.now();
      accepted.forEach((file, i) => {
        const isImage = file.type.startsWith("image/");
        let name = file.name;
        if ((synthesizeNames && isImage) || !name) {
          const ext = (file.type.split("/")[1] || "bin").replace("jpeg", "jpg");
          const suffix = accepted.length > 1 ? `-${i + 1}` : "";
          name = `${isImage ? "screenshot" : "file"}-${stamp}${suffix}.${ext}`;
        }
        this.pendingAttachments.push({
          file,
          name,
          contentType: file.type,
          progress: 0,
          previewUrl: isImage ? URL.createObjectURL(file) : null,
        });
      });
    },
    removePending(index) {
      const att = this.pendingAttachments[index];
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
      this.pendingAttachments.splice(index, 1);
    },
    async uploadPendingAttachments() {
      const uploaded = [];
      for (const att of this.pendingAttachments) {
        const safeName = att.name.replace(/[^\w.-]+/g, "_");
        const path = `skillAuthoring/${
          this.user.uid
        }/${Date.now()}_${safeName}`;
        const ref = storageRef(storage, path);
        att.progress = 0;
        const task = uploadBytesResumable(ref, att.file, {
          contentType: att.contentType,
        });
        await new Promise((resolve, reject) => {
          task.on(
            "state_changed",
            (snap) => {
              att.progress = snap.totalBytes
                ? snap.bytesTransferred / snap.totalBytes
                : 0;
            },
            reject,
            resolve
          );
        });
        att.progress = 1;
        const url = await getDownloadURL(ref);
        if (att.previewUrl) {
          URL.revokeObjectURL(att.previewUrl);
          att.previewUrl = null;
        }
        uploaded.push({ name: att.name, contentType: att.contentType, url });
      }
      return uploaded;
    },
    async send() {
      const text = this.input.trim();
      const hasFiles = this.pendingAttachments.length > 0;
      if ((!text && !hasFiles) || this.sending) return;
      this.error = "";
      this.sending = true;

      let attachments = [];
      if (hasFiles) {
        this.uploadingAttachments = true;
        try {
          attachments = await this.uploadPendingAttachments();
        } catch (e) {
          console.error("attachment upload failed:", e);
          this.error = "Could not upload attachments. Please try again.";
          this.uploadingAttachments = false;
          this.sending = false;
          return;
        }
        this.uploadingAttachments = false;
      }

      // Plain string without attachments; content blocks with them —
      // runClaude's translateContent turns input_file/input_image into
      // Claude document/image blocks (URL source).
      const userMsg = { role: "user", content: text };
      if (attachments.length) {
        const content = [];
        if (text) content.push({ type: "input_text", text });
        for (const a of attachments) {
          if (a.contentType.startsWith("image/")) {
            content.push({ type: "input_image", image_url: a.url });
          } else {
            content.push({ type: "input_file", file_url: a.url });
          }
        }
        userMsg.content = content;
        userMsg.text = text;
        userMsg.attachments = attachments;
      }

      this.messages.push(userMsg);
      this.input = "";
      this.pendingAttachments = [];
      this.scrollDown();
      try {
        const call = httpsCallable(functions, "skillAuthoringChat");
        const res = await call({
          messages: this.messages.map((m) => ({
            role: m.role,
            content: m.content,
          })),
          draft: this.draft || undefined,
        });
        this.messages.push({
          role: "assistant",
          content: (res.data && res.data.text) || "",
        });
      } catch (e) {
        this.error =
          (e && e.message) || "Something went wrong. Please try again.";
      } finally {
        this.sending = false;
        this.scrollDown();
      }
    },
    saveFrom(text) {
      const skill = extractSkillBlock(text);
      if (skill) this.$emit("save", skill);
    },
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.scroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.skill-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--vp-white);
  border-left: 1px solid var(--border-hairline);
}
.skill-chat__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-bottom: 1px solid var(--border-hairline);
}
.skill-chat__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.skill-chat__eyebrow-mark {
  width: 8px;
  height: 8px;
  background: var(--accent-100);
}
.skill-chat__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--vp-black);
}
.skill-chat__close {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--fg-3);
  padding: 4px;
}
.skill-chat__close:hover {
  color: var(--vp-black);
}

.skill-chat__messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.skill-chat__intro {
  color: var(--fg-2);
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.55;
}
.skill-chat__intro-hint {
  margin-top: 10px;
  color: var(--fg-3);
  font-style: italic;
}

.skill-chat__msg-role {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 6px;
}
.skill-chat__msg-body {
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.6;
  color: var(--vp-black);
  white-space: pre-wrap;
  word-break: break-word;
}
.skill-chat__msg--user .skill-chat__msg-body {
  background: var(--bg-muted);
  padding: 10px 12px;
}
.skill-chat__save-row {
  margin-top: 10px;
}
.skill-chat__save {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 16px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.skill-chat__save:hover {
  background: var(--accent-100);
  color: var(--vp-black);
}

.skill-chat__error {
  padding: 10px 20px;
  color: var(--vp-rood-100);
  font-family: var(--font-ui);
  font-size: 13px;
}

/* attachments */
.skill-chat__msg-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.skill-chat__msg-attachment {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--vp-black);
  text-decoration: none;
}
.skill-chat__msg-attachment:hover {
  border-color: var(--vp-black);
}
.skill-chat__file-input {
  display: none;
}
.skill-chat__pending {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 20px 0;
}
.skill-chat__pending-chip {
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px 4px 8px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--vp-black);
}
.skill-chat__pending-chip-progress {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  background: var(--vp-black);
  transition: width 0.18s linear;
}
.skill-chat__pending-chip-thumb {
  width: 28px;
  height: 28px;
  object-fit: cover;
  border-radius: 3px;
  flex-shrink: 0;
}
.skill-chat__pending-chip-name {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.skill-chat__pending-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--fg-3);
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
}
.skill-chat__pending-chip-x:hover {
  color: var(--vp-black);
}
.skill-chat__attach {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  border: 1px solid var(--border-hairline);
  background: transparent;
  color: var(--fg-3);
  cursor: pointer;
  transition: color 0.15s ease;
}
.skill-chat__attach:hover:not(:disabled) {
  color: var(--vp-black);
  border-color: var(--vp-black);
}
.skill-chat__attach:disabled {
  opacity: 0.5;
  cursor: default;
}

.skill-chat__input {
  display: flex;
  gap: 10px;
  padding: 16px 20px 20px;
  border-top: 1px solid var(--border-hairline);
}
.skill-chat__textarea {
  flex: 1;
  resize: none;
  border: 1px solid var(--border-hairline);
  outline: none;
  padding: 10px 12px;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.5;
  color: var(--vp-black);
}
.skill-chat__textarea:focus {
  border-color: var(--vp-black);
}
.skill-chat__send {
  flex-shrink: 0;
  width: 44px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.skill-chat__send:disabled {
  opacity: 0.4;
  cursor: default;
}
</style>
